import React, { Fragment, MouseEvent } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ActivityActionButton } from '../../../components/ActivityActionButton';
import { CompetitionActivityCoverImage } from '../CompetitionActivityCoverImage';
import { CompetitionActivityPodium } from '../CompetitionActivityPodium';
import { getPortalCompetitionDetailsRoute } from '../../helpers';
import { CompetitionActivityBodyProps } from './interfaces';

export const CompetitionActivityBody: React.FC<CompetitionActivityBodyProps> = ({
  slug,
  title,
  status,
  variant,
  description,
  completionMessage,
  managerMessage,
  coverImageId,
  startsOn,
  endsOn,
  disciplineName,
  weaponGroups,
  format,
  podium,
  enrollmentCount,
}): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    navigate(getPortalCompetitionDetailsRoute(slug, status));
  };

  const labelKey =
    variant === 'published' || variant === 'resumed'
      ? 'activities:competition.action.viewAndEnroll'
      : variant === 'completed'
        ? 'activities:competition.action.viewResults'
        : 'activities:competition.action.view';

  const showPodium = variant === 'completed' && podium !== undefined;

  const bodyText =
    variant === 'completed'
      ? completionMessage
      : variant === 'stopped' || variant === 'resumed'
        ? null
        : description;

  const showManagerMessage =
    (variant === 'stopped' || variant === 'resumed') && Boolean(managerMessage);

  const managerMessageIntroKey =
    variant === 'stopped'
      ? 'activities:competition.managerMessage.stoppedIntro'
      : 'activities:competition.managerMessage.resumedIntro';

  const showActionButton = variant !== 'stopped';

  return (
    <Fragment>
      {coverImageId ? (
        <CompetitionActivityCoverImage
          coverImageId={coverImageId}
          title={title}
          variant={variant}
          startsOn={startsOn}
          endsOn={endsOn}
          disciplineName={disciplineName}
          weaponGroups={weaponGroups}
          format={format}
          podium={podium}
          enrollmentCount={enrollmentCount}
        />
      ) : (
        showPodium && (
          <CompetitionActivityPodium
            podium={podium}
            enrollmentCount={enrollmentCount}
          />
        )
      )}

      {showManagerMessage && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2" component="p" sx={{ m: 0 }}>
            {t(managerMessageIntroKey)}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Typography variant="body2" component="p" fontWeight="bold" sx={{ m: 0 }}>
              {t('activities:competition.managerMessage.reasonLabel')}
            </Typography>
            <Typography
              variant="body2"
              component="p"
              sx={{ m: 0, whiteSpace: 'pre-wrap' }}
            >
              {managerMessage}
            </Typography>
          </Box>
        </Box>
      )}

      {bodyText && (
        <Box>
          <Typography variant="body2">{bodyText}</Typography>
        </Box>
      )}

      {showActionButton && (
        <ActivityActionButton
          label={t(labelKey)}
          onClick={handleActionClick}
        />
      )}
    </Fragment>
  );
};
